import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useRememberMe } from '@global/hooks/useRememberMe'
import { api } from 'api'
import { useMfaStore } from 'login/mfa/store/useMfaStore'
import { useLoginFormStore } from 'login/store/useLoginFormStore'
import * as AuthActions from '../../authed/auth.actions'
import * as SnackbarActions from '../../global/snackbar/snackbar.actions'
import { t } from '@lingui/core/macro'
import { AxiosError } from 'axios'

export function useLogin() {
  const dispatch = useDispatch()
  const history = useHistory()
  const [loading, setLoading] = useState(false)

  const {
    email,
    password,
    remember_me,
    mfa_code,
    showPassword,
    setLoginForm,
    setShowPassword,
    resetLoginStore,
  } = useLoginFormStore()

  const { setMfaRequired, setMfaSuccessful, isMfaRequired } = useMfaStore()

  const loginForm = { email, password, remember_me, mfa_code }

  const { setRememberedUsername } = useRememberMe({
    onRemberedUsername: rememberedUsername => {
      setLoginForm({
        ...loginForm,
        email: rememberedUsername,
        remember_me: true,
      })
    },
  })

  function handleRememberMe() {
    if (remember_me) setRememberedUsername(email)
    else setRememberedUsername(null)
  }

  function handleLoginFormChange(name: string, value: string) {
    setLoginForm({
      [name]: value,
    })
  }

  function handleLogin(e: React.FormEvent<HTMLFormElement>, isMfaLogin = false) {
    e.preventDefault()
    setLoading(true)

    const loginForm = { email, password, remember_me, mfa_code }

    api
      .post(`/login`, loginForm, { withCredentials: true })
      .then(res => {
        handleRememberMe()
        const fullToe = res.headers.toe || ''
        const sessionToe = fullToe.split('-')[0]

        if (isMfaRequired) {
          setMfaSuccessful(true)
        }

        resetLoginStore()
        dispatch(AuthActions.getUser(sessionToe))
        setLoading(false)
      })
      .catch((error: unknown) => {
        if (!(error instanceof AxiosError)) {
          return
        }
        const typedError = error as AxiosError<
          | {
              message?: string
              errors?: string[]
              mfa_required?: boolean
              mfa_token?: string
              is_password_expired?: boolean
              reset_password_token?: string
              user_initiated?: boolean
            }
          | undefined
        > // TODO: add AxiosError types globally, utilize here
        const data = typedError.response?.data ?? {}
        setLoading(false)

        if (isMfaLogin) {
          if (isMfaRequired && error.status === 428) {
            dispatch(
              SnackbarActions.showSnackbar({
                message: t`Invalid MFA code. Please try again.`,
                type: 'ERROR',
              }),
            )
            return
          }
        }

        if (data.mfa_required && error.status === 428) {
          setMfaRequired(true)

          setShowPassword(false)
          history.push(
            `/auth/mfa${location.search || '?'}&email=${encodeURIComponent(
              email ?? '',
            )}&mfa_token=${data.mfa_token ?? ''}`,
          )
          return
        }

        if (data.is_password_expired) {
          let url = `/auth/reset-password/${data.reset_password_token ?? ''}?userInitiated=${String(data.user_initiated ?? '')}`
          if (location.href.includes('login=system')) {
            url += '&login=system'
          }
          history.push(url)
        }
        dispatch(
          SnackbarActions.showSnackbar({
            message:
              data.message ??
              (error.message ? error.message : t`Could not log in. Please try again.`),
            type: 'ERROR',
          }),
        )
      })
  }

  return {
    setShowPassword,
    showPassword,
    loading,
    loginForm,
    handleLogin,
    handleLoginFormChange,
  }
}
